import { getPremiumOptionLabel } from '@griply/utils';
import { useCreateCalendarEventAllowed } from 'features/external-connections';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar16,
  CheckCircle16,
  FilterMinimalStyleCircle16,
  TagEmpty16,
  Target,
} from 'shared/assets/icons';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import { SelectSmartListsContextMenu } from 'shared/components/connected/select-smart-lists-context-menu';
import { SelectTagsContextMenu } from 'shared/components/connected/select-tags-context-menu';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { ID } from 'shared/types/id';
import { LinkOptions } from 'shared/types/task';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

const shortcutKeys = [
  ['Digit1'],
  ['Digit2'],
  ['Digit3'],
  ['Digit4'],
  ['Digit5'],
];

enum SlotSubMenu {
  LinkedItem,
  SmartList,
  Tag,
}

export type CalendarSlotContextMenuProps = {
  slot: { anchor: { top: number; left: number } } | null;
  onClose: () => void;
  onNewTask: () => void;
  onNewEvent: () => void;
  onSelectLinkedItem: (link: { id: ID; type: LinkOptions } | null) => void;
  onSelectSmartList: (id: ID) => void;
  onSelectTag: (id: ID) => void;
};

export const CalendarSlotContextMenu: React.FC<
  CalendarSlotContextMenuProps
> = ({
  slot,
  onClose,
  onNewTask,
  onNewEvent,
  onSelectLinkedItem,
  onSelectSmartList,
  onSelectTag,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [subMenu, setSubMenu] = useState<SlotSubMenu | null>(null);
  const { allowed: createEventAllowed } = useCreateCalendarEventAllowed();

  const newEventLabel = getPremiumOptionLabel(
    t('context-menus.calendar-slot.labels.new-event'),
    createEventAllowed,
    t,
  );

  const openLinkedItem = () => setSubMenu(SlotSubMenu.LinkedItem);
  const openSmartList = () => setSubMenu(SlotSubMenu.SmartList);
  const openTag = () => setSubMenu(SlotSubMenu.Tag);

  // The goal tree menu reports a target container: a clicked goal arrives with its id
  // as the last `parentIds` entry; a clicked life area arrives via `lifeAreaId`.
  const onSelectMoveTarget = (parentIds: ID[], lifeAreaId: ID | null) => {
    if (parentIds.length) {
      onSelectLinkedItem({
        id: parentIds[parentIds.length - 1],
        type: LinkOptions.Goal,
      });
      return;
    }
    if (lifeAreaId) {
      onSelectLinkedItem({ id: lifeAreaId, type: LinkOptions.LifeArea });
      return;
    }
    onSelectLinkedItem(null);
  };

  const onNumberShortcut = useCallback(
    (e: KeyboardEvent, keys: string[]) => {
      // Root menu only — the picker sub-menus own their own key handling.
      if (!slot || subMenu !== null) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (keys.includes('Digit1')) {
        onNewTask();
        return;
      }
      if (keys.includes('Digit2')) {
        onNewEvent();
        return;
      }
      if (keys.includes('Digit3')) {
        setSubMenu(SlotSubMenu.LinkedItem);
        return;
      }
      if (keys.includes('Digit4')) {
        setSubMenu(SlotSubMenu.SmartList);
        return;
      }
      if (keys.includes('Digit5')) {
        setSubMenu(SlotSubMenu.Tag);
      }
    },
    [slot, subMenu, onNewTask, onNewEvent],
  );

  useKeysDown(shortcutKeys, onNumberShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  // Bails on a null ref, so it stays inert while a sub-menu (which owns its own
  // arrow handling) has replaced the root menu.
  useFocusChildrenWithArrows(containerRef);

  if (!slot) {
    return null;
  }

  if (subMenu === SlotSubMenu.LinkedItem) {
    return (
      <GoalTreeContextMenu
        location={slot.anchor}
        onSelect={onSelectMoveTarget}
        onClose={onClose}
      />
    );
  }

  if (subMenu === SlotSubMenu.SmartList) {
    return (
      <SelectSmartListsContextMenu
        location={slot.anchor}
        onSmartList={onSelectSmartList}
        onClose={onClose}
      />
    );
  }

  if (subMenu === SlotSubMenu.Tag) {
    return (
      <SelectTagsContextMenu
        location={slot.anchor}
        activeTags={[]}
        onTag={onSelectTag}
        onClose={onClose}
      />
    );
  }

  return (
    <PopupMenu ref={containerRef} location={slot.anchor} onOutside={onClose}>
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={CheckCircle16} />}
            onClick={onNewTask}
            shortcut={getPlatformShortcut(['1'])}
          >
            {t('context-menus.calendar-slot.labels.new-task')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={Calendar16} />}
            onClick={onNewEvent}
            shortcut={getPlatformShortcut(['2'])}
          >
            {newEventLabel}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList>
        <PopupMenuTitle
          title={t('context-menus.calendar-slot.section.block-time-title')}
          isSubTitle
        />
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={Target} />}
            onClick={openLinkedItem}
            shortcut={getPlatformShortcut(['3'])}
          >
            {t('context-menus.calendar-slot.labels.linked-item')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={FilterMinimalStyleCircle16} />}
            onClick={openSmartList}
            shortcut={getPlatformShortcut(['4'])}
          >
            {t('context-menus.calendar-slot.labels.smart-list')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={TagEmpty16} />}
            onClick={openTag}
            shortcut={getPlatformShortcut(['5'])}
          >
            {t('context-menus.calendar-slot.labels.tag')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
